import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { WorshipSongAnalysisJSON } from '../lib/openai';

export interface WorshipSongAnalysisRecord {
  id: string;
  title: string;
  content: WorshipSongAnalysisJSON;
  created_at: string;
  updated_at: string;
}

interface CreateWorshipSongAnalysisData {
  title: string;
  content: WorshipSongAnalysisJSON;
}

export function useWorshipSongAnalysis() {
  const [analyses, setAnalyses] = useState<WorshipSongAnalysisRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('worship_song_analyses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAnalyses(data || []);
    } catch (err) {
      console.error('Error loading worship song analyses:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyses();
  }, []);

  const saveAnalysis = async (data: CreateWorshipSongAnalysisData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: saved, error: saveError } = await supabase
      .from('worship_song_analyses')
      .insert([{ ...data, user_id: user.id }])
      .select()
      .single();

    if (saveError) throw saveError;
    await loadAnalyses();
    return saved as WorshipSongAnalysisRecord;
  };

  const deleteAnalysis = async (id: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('worship_song_analyses')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (deleteError) throw deleteError;
    await loadAnalyses();
  };

  return {
    analyses,
    loading,
    error,
    saveAnalysis,
    deleteAnalysis,
    refresh: loadAnalyses,
  };
}
